import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { logoutApi } from '../../services/authService';
import { getProfile, newGetNotifications } from '../../services/userService';
import { useNotification } from '../../context/NotificationContext';

const DefaultProfile = '/images/profile/default-profile.png';

function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const { unreadCount, updateUnreadCount, getLastManualUpdate } = useNotification();

  const [profileData, setProfileData] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [notificationList, setNotificationList] = useState([]);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const dropdownRef = useRef(null);
  const moreMenuRef = useRef(null);
  const notificationRef = useRef(null);

  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;

  // All navigation items — My Course page has tabs: My Course, Explore Courses, Certificates
  const navItems = [
    { label: 'Home', path: '/user/dashboard', icon: 'fluent:grid-20-regular', iconFilled: 'fluent:grid-20-filled' },
    { label: 'Loop', path: '/user/feed', icon: 'fluent:news-20-regular', iconFilled: 'fluent:news-20-filled' },
    { label: 'Courses', path: '/user/courses', icon: 'fluent:book-20-regular', iconFilled: 'fluent:book-20-filled' },
    { label: 'Live', path: '/user/AllClassroom', icon: 'fluent:people-20-regular', iconFilled: 'fluent:people-20-filled' },
    { label: 'Survey', path: '/user/survey', icon: 'fluent:clipboard-task-list-ltr-20-regular', iconFilled: 'fluent:clipboard-task-list-ltr-20-filled' },
    { label: 'Career Path', path: '/user/career', icon: 'fluent:briefcase-20-regular', iconFilled: 'fluent:briefcase-20-filled' },
    { label: 'Patient Resources', path: '/user/patient-resources', icon: 'fluent:document-heart-20-regular', iconFilled: 'fluent:document-heart-20-filled' },
    { label: 'Documents', path: '/user/organization-docs', icon: 'fluent:document-20-regular', iconFilled: 'fluent:document-20-filled' },
  ];

  // Bottom tab items (first 4 + More)
  const bottomTabItems = navItems.slice(0, 4);
  // Items in the "More" menu
  const moreMenuItems = navItems.slice(4);

  const isActive = (path) => {
    if (path === '/user/courses') {
      return location.pathname.startsWith('/user/courses');
    }
    if (path === '/user/AllClassroom' && (
      location.pathname.startsWith('/user/AllClassroom') ||
      location.pathname.startsWith('/user/assignmentsQuestions')
    )) {
      return true;
    }
    if (path === '/user/survey') {
      return location.pathname === '/user/survey';
    }
    if (path === '/user/career') {
      return location.pathname === '/user/career';
    }
    if (path === '/user/patient-resources') {
      return location.pathname === '/user/patient-resources';
    }
    if (path === '/user/organization-docs') {
      return location.pathname === '/user/organization-docs';
    }
    return location.pathname === path;
  };

  const fetchProfile = async () => {
    try {
      const response = await getProfile();
      setProfileData(response.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const fetchNotifications = async (skipIfRecent = false) => {
    if (skipIfRecent && Date.now() - getLastManualUpdate() < 5000) {
      return;
    }
    try {
      const response = await newGetNotifications({});
      if (response && response.notifications) {
        const unread = response.notifications.filter((n) => n.is_read === 0).length;
        updateUnreadCount(unread);
      } else {
        updateUnreadCount(0);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  useEffect(() => {
    fetchProfile();
    fetchNotifications();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => fetchNotifications(true), 30000);
    return () => clearInterval(interval);
  }, []);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setIsMoreMenuOpen(false);
    setIsDropdownOpen(false);
    setIsNotificationOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target)) {
        setIsMoreMenuOpen(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setIsNotificationOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNotificationList = async () => {
    setNotificationLoading(true);
    try {
      const response = await newGetNotifications({ page: 1, limit: 8 });
      if (response && response.notifications) {
        setNotificationList(response.notifications);
      } else {
        setNotificationList([]);
      }
    } catch (error) {
      setNotificationList([]);
    } finally {
      setNotificationLoading(false);
    }
  };

  const handleNotificationToggle = () => {
    const next = !isNotificationOpen;
    setIsNotificationOpen(next);
    if (next) fetchNotificationList();
  };

  const formatNotificationDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  // Prevent body scroll when more menu is open
  useEffect(() => {
    if (isMoreMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMoreMenuOpen]);

  const handleLogout = async () => {
    try {
      await logoutApi();
      localStorage.clear();
      window.location.href = '/auth/login';
    } catch (error) {
      console.error('Logout error:', error);
      localStorage.clear();
      window.location.href = '/auth/login';
    }
  };

  const getAccountTypeLabel = () => {
    const role = (typeof window !== 'undefined' && localStorage.getItem('role')) || '';
    switch (role.toLowerCase()) {
      case 'admin':
      case 'superadmin':
        return 'Admin';
      case 'trainer':
        return 'Trainer';
      case 'teacher':
        return 'Teacher';
      case 'trainee':
      case 'learner':
        return 'Learner';
      default:
        return '';
    }
  };

  const getDisplayName = () => {
    if (!profileData) return '';
    return (
      profileData.name ||
      profileData.fullName ||
      profileData.full_name ||
      profileData.username ||
      profileData.user_name ||
      profileData.email ||
      ''
    );
  };

  const accountTypeLabel = getAccountTypeLabel();
  const displayName = getDisplayName();

  // Desktop / Tablet Header
  const renderDesktopHeader = () => {
    return (
      <div className="flex flex-col lg:grid lg:grid-cols-[1fr_auto_1fr] items-center gap-4 lg:gap-6">
        {/* Top row on tablet: logo + actions in one row; on desktop this is the full grid */}
        <div className="flex w-full items-center justify-between lg:contents">
          {/* Left: logo */}
          <div className="shrink-0">
            <Link to="/user/dashboard" className="inline-block">
              <img
                src="/images/logos/gomedulla_black.png"
                alt="GoMedulla"
                className="h-5 sm:h-6 md:h-7 lg:h-8 w-auto max-w-[120px] sm:max-w-[180px] lg:max-w-[200px] object-contain"
              />
            </Link>
          </div>

          {/* Center: nav bar (hidden on small screens, shown from tablet up) */}
          <div className="hidden md:flex justify-center min-w-0 flex-1 overflow-hidden">
            <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-2xl border border-black/[0.06] bg-white overflow-x-auto max-w-full scrollbar-thin">
              <nav className="inline-flex items-center gap-1">
                {navItems.map((item) => {
                  const active = isActive(item.path);
                  return (
                    <Link
                      key={item.label}
                      to={item.path}
                      className={`inline-flex items-center ${isTablet ? 'px-2.5 py-1 text-[13px]' : 'px-3 xl:px-3.5 py-1 text-[13px]'} rounded-[10px] font-semibold no-underline transition-all duration-200 whitespace-nowrap shrink-0 focus:outline-none focus-visible:outline-none border ${
                        active ? 'text-white bg-primary border-primary hover:text-white' : 'text-gray-600 bg-transparent hover:bg-transparent hover:text-primary border-transparent hover:border-primary'
                      }`}
                    >
                      {item.label}
                    </Link>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Right: actions */}
          <div className="flex items-center gap-2 sm:gap-3 justify-end shrink-0">
            {/* Notifications dropdown - show list below icon */}
            <div className="relative" ref={notificationRef}>
              <button
                type="button"
                onClick={handleNotificationToggle}
                className="flex items-center justify-center w-9 h-9 rounded-xl bg-gray-50 border border-black/[0.05] cursor-pointer transition-colors hover:bg-gray-100 relative"
                aria-label="Notifications"
              >
                <Icon icon="fluent:alert-20-regular" width="20" height="20" className="text-gray-600" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>
              {isNotificationOpen && (
                <div className="absolute right-0 mt-2 w-[320px] max-h-[400px] overflow-hidden rounded-xl bg-white shadow-[0_10px_40px_rgba(0,0,0,0.12)] border border-gray-200 z-50 flex flex-col">
                  <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between shrink-0">
                    <span className="font-semibold text-gray-800">Notifications</span>
                <Link
                  to="/user/notifications"
                  onClick={() => setIsNotificationOpen(false)}
                  className="text-sm text-[#7083FF] no-underline hover:underline"
                >
                      See all
                    </Link>
                  </div>
                  <div className="overflow-y-auto flex-1 min-h-0">
                    {notificationLoading ? (
                      <div className="p-4 text-center text-gray-500 text-sm">Loading...</div>
                    ) : notificationList.length === 0 ? (
                      <div className="p-4 text-center text-gray-500 text-sm">No notifications</div>
                    ) : (
                      notificationList.map((n) => (
                        <Link
                          key={n.id}
                          to="/user/notifications"
                          onClick={() => setIsNotificationOpen(false)}
                          className={`block px-4 py-3 text-left no-underline border-b border-gray-50 last:border-b-0 hover:bg-gray-50 ${!n.is_read ? 'bg-blue-50/50' : ''}`}
                        >
                          <div className="font-medium text-gray-800 text-sm line-clamp-1">{n.title}</div>
                          <p className="text-gray-600 text-xs mt-0.5 line-clamp-2">{n.body}</p>
                          <div className="text-gray-400 text-[11px] mt-1">{formatNotificationDate(n.createdAt)}</div>
                        </Link>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Search icon - commented for now */}
            {/* <button
              type="button"
              className="flex items-center justify-center w-10 h-10 rounded-xl bg-gray-100 border-none cursor-pointer transition-colors hover:bg-gray-200"
              aria-label="Search"
            >
              <Icon icon="mdi:magnify" width="20" height="20" className="text-gray-600" />
            </button> */}

            {/* Profile dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setIsDropdownOpen((prev) => !prev)}
                className="flex items-center gap-2 p-0 bg-white px-2 py-1 rounded-xl cursor-pointer border border-black/[0.06] hover:border-black/[0.10] transition"
              >
                <img
                  src={profileData?.profile_pic_url || DefaultProfile}
                  alt="User"
                  className="w-7 h-7 rounded-lg object-cover"
                />
                {(displayName || accountTypeLabel) && (
                  <div className="hidden sm:flex flex-col items-start max-w-[160px]">
                    {displayName && (
                      <span className="text-[16px] font-medium text-gray-700 leading-tight truncate w-full">
                        {displayName}
                      </span>
                    )}
                    {accountTypeLabel && (
                      <span className="inline-flex items-center font-medium text-[10px] text-gray-400 rounded-full">
                        {accountTypeLabel}
                      </span>
                    )}
                  </div>
                )}
                <Icon icon={isDropdownOpen ? 'mdi:chevron-up' : 'mdi:chevron-down'} width="20" height="20" className="text-gray-600" />
              </button>

              {isDropdownOpen && (
                <div className="absolute right-0 mt-2 w-[200px] rounded-xl bg-white shadow-[0_10px_40px_rgba(0,0,0,0.12)] border border-gray-200 py-2 z-50">
                  <Link
                    to="/user/profile"
                    onClick={() => setIsDropdownOpen(false)}
                    className="block px-4 py-2.5 text-sm text-gray-700 no-underline hover:bg-gray-50"
                  >
                    Profile
                  </Link>
                  <Link
                    to="/user/settings"
                    onClick={() => setIsDropdownOpen(false)}
                    className="block px-4 py-2.5 text-sm text-gray-700 no-underline hover:bg-gray-50"
                  >
                    Settings
                  </Link>
                  <Link
                    to="/user/help"
                    onClick={() => setIsDropdownOpen(false)}
                    className="block px-4 py-2.5 text-sm text-gray-700 no-underline hover:bg-gray-50"
                  >
                    Help
                  </Link>
                  <div className="h-px bg-gray-200 my-1" />
                  <button
                    type="button"
                    onClick={() => { setIsDropdownOpen(false); handleLogout(); }}
                    className="block px-4 py-2.5 text-sm text-red-600 bg-transparent border-none cursor-pointer w-full text-left hover:bg-red-50"
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Mobile Header (minimal - just logo and actions)
  const renderMobileHeader = () => (
    <>
      <div className="flex items-center justify-between">
        {/* Logo left */}
        <Link to="/user/dashboard" className="inline-block">
          <img
            src="/images/logos/gomedulla_black.png"
            alt="GoMedulla"
            className="h-5 w-auto max-h-5 max-w-[120px] object-contain"
          />
        </Link>

        {/* Right: notifications + profile (search icon commented out) */}
        <div className="flex items-center gap-2">
          {/* Search icon - commented for now */}
          {/* <button
            type="button"
            className="flex items-center justify-center w-9 h-9 rounded-full bg-gray-100 border-none cursor-pointer"
            aria-label="Search"
          >
            <Icon icon="fluent:search-20-regular" width="18" height="18" className="text-gray-600" />
          </button> */}
          
          {/* Notifications dropdown - show list below icon */}
          <div className="relative" ref={notificationRef}>
            <button
              type="button"
              onClick={handleNotificationToggle}
              className="flex items-center justify-center w-9 h-9 rounded-full bg-gray-100 border-none cursor-pointer relative"
              aria-label="Notifications"
            >
              <Icon icon="fluent:alert-20-regular" width="18" height="18" className="text-gray-600" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[9px] font-semibold px-1.5 py-0.5 rounded-full min-w-[16px] text-center">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>
            {isNotificationOpen && (
              <div className="absolute right-0 mt-2 w-[min(320px,calc(100vw-2rem))] max-h-[360px] overflow-hidden rounded-xl bg-white shadow-[0_10px_40px_rgba(0,0,0,0.15)] border border-gray-200 z-[100] flex flex-col">
                <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between shrink-0">
                  <span className="font-semibold text-gray-800">Notifications</span>
                  <Link
                    to="/user/notifications"
                    onClick={() => setIsNotificationOpen(false)}
                    className="text-sm text-[#7083FF] no-underline hover:underline"
                  >
                    See all
                  </Link>
                </div>
                <div className="overflow-y-auto flex-1 min-h-0">
                  {notificationLoading ? (
                    <div className="p-4 text-center text-gray-500 text-sm">Loading...</div>
                  ) : notificationList.length === 0 ? (
                    <div className="p-4 text-center text-gray-500 text-sm">No notifications</div>
                  ) : (
                    notificationList.map((n) => (
                      <Link
                        key={n.id}
                        to="/user/notifications"
                        onClick={() => setIsNotificationOpen(false)}
                        className={`block px-4 py-3 text-left no-underline border-b border-gray-50 last:border-b-0 hover:bg-gray-50 ${!n.is_read ? 'bg-blue-50/50' : ''}`}
                      >
                        <div className="font-medium text-gray-800 text-sm line-clamp-1">{n.title}</div>
                        <p className="text-gray-600 text-xs mt-0.5 line-clamp-2">{n.body}</p>
                        <div className="text-gray-400 text-[11px] mt-1">{formatNotificationDate(n.createdAt)}</div>
                      </Link>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
          
          <button
            type="button"
            onClick={() => setIsDropdownOpen((prev) => !prev)}
            className="p-0 border-none bg-transparent cursor-pointer"
          >
            <img
              src={profileData?.profile_pic_url || DefaultProfile}
              alt="User"
              className="w-9 h-9 rounded-full object-cover border-2 border-gray-200"
            />
          </button>
        </div>
      </div>

      {/* Profile dropdown */}
      {isDropdownOpen && (
        <div
          ref={dropdownRef}
          className="absolute top-14 right-4 w-[220px] rounded-2xl bg-white shadow-[0_10px_40px_rgba(0,0,0,0.15)] border border-gray-200 p-2 z-[100]"
        >
          {/* User info */}
          <div className="p-3 border-b border-gray-100 mb-2">
            <div className="flex items-center gap-3">
              <img
                src={profileData?.profile_pic_url || DefaultProfile}
                alt="User"
                className="w-10 h-10 rounded-full object-cover"
              />
              <div>
                <div className="text-sm font-semibold text-gray-800">
                  {profileData?.name || 'User'}
                </div>
                <div className="text-xs text-gray-500">
                  View profile
                </div>
              </div>
            </div>
          </div>
          
          <Link
            to="/user/profile"
            onClick={() => setIsDropdownOpen(false)}
            className="flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 no-underline rounded-[10px] hover:bg-gray-50"
          >
            <Icon icon="fluent:person-20-regular" width="20" className="text-gray-500" />
            <span>My Profile</span>
          </Link>
          <Link
            to="/user/settings"
            onClick={() => setIsDropdownOpen(false)}
            className="flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 no-underline rounded-[10px] hover:bg-gray-50"
          >
            <Icon icon="fluent:settings-20-regular" width="20" className="text-gray-500" />
            <span>Settings</span>
          </Link>
          <Link
            to="/user/help"
            onClick={() => setIsDropdownOpen(false)}
            className="flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 no-underline rounded-[10px] hover:bg-gray-50"
          >
            <Icon icon="fluent:question-circle-20-regular" width="20" className="text-gray-500" />
            <span>Help</span>
          </Link>
          <div className="h-px bg-gray-100 my-2" />
          <button
            type="button"
            onClick={() => { setIsDropdownOpen(false); handleLogout(); }}
            className="flex items-center gap-3 px-3 py-2.5 text-sm text-red-600 bg-transparent border-none cursor-pointer w-full rounded-[10px] hover:bg-red-50"
          >
            <Icon icon="fluent:sign-out-20-regular" width="20" />
            <span>Logout</span>
          </button>
        </div>
      )}
    </>
  );

  // Bottom Tab Bar for Mobile
  const renderBottomTabBar = () => {
    const isMoreActive = moreMenuItems.some(item => isActive(item.path));
    
    return (
      <>
        {/* Bottom Tab Bar */}
        <nav
          className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 flex justify-around items-center px-1 py-2 z-[100] shadow-[0_-2px_12px_rgba(0,0,0,0.05)]"
          style={{ paddingBottom: 'calc(8px + env(safe-area-inset-bottom, 0px))' }}
        >
          {bottomTabItems.map((item) => {
            const active = isActive(item.path);
            return (
              <Link
                key={item.label}
                to={item.path}
                className={`group flex flex-col items-center gap-1 px-3 py-1.5 no-underline rounded-xl transition-all duration-200 border ${
                  active ? 'bg-primary text-white border-primary' : 'bg-transparent border-transparent hover:text-primary hover:border-primary'
                }`}
              >
                <Icon
                  icon={active ? item.iconFilled : item.icon}
                  width="24"
                  height="24"
                  className={active ? 'text-white' : 'text-gray-500 group-hover:text-primary'}
                />
                <span className={`text-[11px] ${active ? 'font-semibold text-white' : 'font-medium text-gray-500 group-hover:text-primary'}`}>
                  {item.label}
                </span>
              </Link>
            );
          })}
          
          {/* More tab — only when there are items in the More menu */}
          {moreMenuItems.length > 0 && (
            <button
              type="button"
              onClick={() => setIsMoreMenuOpen(true)}
              className={`flex flex-col items-center gap-1 px-3 py-1.5 border-none cursor-pointer rounded-xl border ${
                isMoreActive ? 'bg-primary text-white border-primary' : 'bg-transparent border-transparent'
              }`}
            >
              <Icon
                icon={isMoreActive ? 'fluent:more-horizontal-20-filled' : 'fluent:more-horizontal-20-regular'}
                width="24"
                height="24"
                className={isMoreActive ? 'text-white' : 'text-gray-500'}
              />
              <span className={`text-[11px] ${isMoreActive ? 'font-semibold text-white' : 'font-medium text-gray-500'}`}>
                More
              </span>
            </button>
          )}
        </nav>

        {/* More Bottom Sheet */}
        {isMoreMenuOpen && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/40 z-[998] backdrop-blur-sm"
              onClick={() => setIsMoreMenuOpen(false)}
            />
            
            {/* Bottom Sheet */}
            <div
              ref={moreMenuRef}
              className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-[999] shadow-[0_-10px_40px_rgba(0,0,0,0.15)] animate-slideUp"
              style={{ paddingBottom: 'env(safe-area-inset-bottom, 20px)' }}
            >
              {/* Handle bar */}
              <div className="flex justify-center p-3">
                <div className="w-10 h-1 bg-gray-300 rounded-full" />
              </div>
              
              {/* Sheet header */}
              <div className="px-5 pb-4 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-gray-800 m-0">More</h3>
              </div>
              
              {/* Menu items */}
              <div className="p-3">
                {moreMenuItems.map((item) => {
                  const active = isActive(item.path);
                  return (
                    <Link
                      key={item.label}
                      to={item.path}
                      onClick={() => setIsMoreMenuOpen(false)}
                      className={`flex items-center gap-4 px-4 py-4 text-base font-medium no-underline rounded-[14px] my-1 border ${
                        active ? 'text-primary bg-primary/10 border-primary' : 'text-gray-700 bg-transparent border-transparent hover:text-primary hover:border-primary'
                      }`}
                    >
                      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                        active ? 'bg-primary' : 'bg-gray-100'
                      }`}>
                        <Icon
                          icon={active ? item.iconFilled : item.icon}
                          width="24"
                          height="24"
                          className={active ? 'text-white' : 'text-gray-500'}
                        />
                      </div>
                      {item.label}
                    </Link>
                  );
                })}
                
                {/* Divider */}
                <div className="h-px bg-gray-100 mx-4 my-3" />
                
                {/* Extra options */}
                <Link
                  to="/user/profile"
                  onClick={() => setIsMoreMenuOpen(false)}
                  className="flex items-center gap-4 px-4 py-4 text-base font-medium no-underline text-gray-700 rounded-[14px]"
                >
                  <div className="w-11 h-11 rounded-xl bg-gray-100 flex items-center justify-center">
                    <Icon icon="fluent:person-20-regular" width="24" height="24" className="text-gray-500" />
                  </div>
                  Profile
                </Link>
                
                <Link
                  to="/user/settings"
                  onClick={() => setIsMoreMenuOpen(false)}
                  className="flex items-center gap-4 px-4 py-4 text-base font-medium no-underline text-gray-700 rounded-[14px]"
                >
                  <div className="w-11 h-11 rounded-xl bg-gray-100 flex items-center justify-center">
                    <Icon icon="fluent:settings-20-regular" width="24" height="24" className="text-gray-500" />
                  </div>
                  Settings
                </Link>
                
                <Link
                  to="/user/help"
                  onClick={() => setIsMoreMenuOpen(false)}
                  className="flex items-center gap-4 px-4 py-4 text-base font-medium no-underline text-gray-700 rounded-[14px]"
                >
                  <div className="w-11 h-11 rounded-xl bg-gray-100 flex items-center justify-center">
                    <Icon icon="fluent:question-circle-20-regular" width="24" height="24" className="text-gray-500" />
                  </div>
                  Help
                </Link>
                
                <button
                  type="button"
                  onClick={() => { setIsMoreMenuOpen(false); handleLogout(); }}
                  className="flex items-center gap-4 px-4 py-4 text-base font-medium text-red-600 bg-transparent border-none cursor-pointer w-full rounded-[14px]"
                >
                  <div className="w-11 h-11 rounded-xl bg-red-50 flex items-center justify-center">
                    <Icon icon="fluent:sign-out-20-regular" width="24" height="24" className="text-red-600" />
                  </div>
                  Logout
                </button>
              </div>
            </div>
          </>
        )}

        {/* CSS animations */}
        <style>{`
          @keyframes slideUp {
            from { transform: translateY(100%); }
            to { transform: translateY(0); }
          }
          .animate-slideUp {
            animation: slideUp 0.3s ease-out;
          }
        `}</style>
      </>
    );
  };

  return (
    <>
      <header className="bg-transparent relative">
        {/* Main header */}
        <div className={isMobile ? 'px-4 py-1' : 'px-6 py-1'}>
          {isMobile ? renderMobileHeader() : renderDesktopHeader()}
        </div>
      </header>

      {/* Bottom Tab Bar - only on mobile */}
      {isMobile && renderBottomTabBar()}
    </>
  );
}

export default Header;
